/**
 * Factor types that can appear in an `mfa_required` error's `mfa_requirements` payload.
 */
export type MfaRequiredFactorType =
  | 'otp'
  | 'oob'
  | 'email'
  | 'webauthn-roaming'
  | 'webauthn-platform'
  | 'push-notification'
  | 'phone';

/**
 * Describes which MFA factors the user must challenge or enroll.
 */
export interface MfaRequirements {
  challenge?: Array<{ type: MfaRequiredFactorType }>;
  enroll?: Array<{ type: MfaRequiredFactorType }>;
}

/**
 * Interface to represent an OAuth2 error.
 * When the error is `mfa_required`, `mfa_token` and `mfa_requirements` will be populated.
 */
export interface OAuth2Error {
  error: string;
  error_description: string;
  message?: string;
  mfa_token?: string;
  mfa_requirements?: MfaRequirements;
}

/**
 * Normalizes an unknown error (typically thrown by `openid-client`) into an
 * {@link OAuth2Error}.
 *
 * On an `mfa_required` response, `openid-client` nests the server's `mfa_token`
 * and `mfa_requirements` under `error.cause`; this lifts them to the top level
 * so {@link isMfaRequiredError} can detect the error and callers can continue
 * with the MFA APIs.
 *
 * @internal
 */
export function toOAuth2Error(e: unknown): OAuth2Error {
  if (typeof e !== 'object' || e === null) {
    return { error: 'unknown_error', error_description: String(e) };
  }
  const err = e as { error?: string; error_description?: string; cause?: Record<string, unknown>; message?: string };
  const base: OAuth2Error = {
    error: err.error ?? '',
    error_description: err.error_description ?? '',
    message: err.message,
  };
  if (err.error === 'mfa_required' && err.cause) {
    base.mfa_token = typeof err.cause.mfa_token === 'string' ? err.cause.mfa_token : undefined;
    const req = err.cause.mfa_requirements;
    if (typeof req === 'object' && req !== null) {
      base.mfa_requirements = req as OAuth2Error['mfa_requirements'];
    }
  }
  return base;
}

/**
 * Error codes used for {@link NotSupportedError}
 */
export enum NotSupportedErrorCode {
  PAR_NOT_SUPPORTED = 'par_not_supported_error',
  MTLS_WITHOUT_CUSTOMFETCH_NOT_SUPPORT = 'mtls_without_custom_fetch_not_supported',
}

/**
 * Error thrown when a feature is not supported.
 * For example, when trying to use Pushed Authorization Requests (PAR) but the Auth0 tenant was not configured to support it.
 */
export class NotSupportedError extends Error {
  public code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'NotSupportedError';
    this.code = code;
  }
}

/**
 * Base class for API errors, containing the error, error_description and message (if available).
 */
abstract class ApiError extends Error {
  public cause?: OAuth2Error;
  public code: string;

  constructor(code: string, message: string, cause?: OAuth2Error) {
    super(message);

    this.code = code;
    this.cause = cause && {
      error: cause.error,
      error_description: cause.error_description,
      message: cause.message,
      mfa_token: cause.mfa_token,
      mfa_requirements: cause.mfa_requirements,
    };
  }
}

/**
 * Error thrown when trying to get an access token.
 */
export class TokenByCodeError extends ApiError {
  constructor(message: string, cause?: OAuth2Error) {
    super('token_by_code_error', message, cause);
    this.name = 'TokenByCodeError';
  }
}

/**
 * Error thrown when trying to get an access token.
 */
export class TokenByClientCredentialsError extends ApiError {
  constructor(message: string, cause?: OAuth2Error) {
    super('token_by_client_credentials_error', message, cause);
    this.name = 'TokenByClientCredentialsError';
  }
}

/**
 * Error thrown when trying to get an access token.
 */
export class TokenByRefreshTokenError extends ApiError {
  constructor(message: string, cause?: OAuth2Error) {
    super('token_by_refresh_token_error', message, cause);
    this.name = 'TokenByRefreshTokenError';
  }
}

/**
 * Error thrown when trying to get an access token using Resource Owner Password Grant.
 */
export class TokenByPasswordError extends ApiError {
  constructor(message: string, cause?: OAuth2Error) {
    super('token_by_password_error', message, cause);
    this.name = 'TokenByPasswordError';
  }
}

/**
 * Error thrown when trying to get an access token for a connection.
 *
 * @deprecated Since v1.2.0, using {@link AuthClient#getTokenForConnection} is deprecated and we recommend to use {@link AuthClient#exchangeToken}.
 * When doing so, use {@link TokenExchangeError} instead of {@link TokenForConnectionError}.
 * This error class remains for backward compatibility and is planned for removal in v2.0.
 */
export class TokenForConnectionError extends ApiError {
  constructor(message: string, cause?: OAuth2Error) {
    super('token_for_connection_error', message, cause);
    // NOTE: The name is 'TokenForConnectionErrorCode' (not 'TokenForConnectionError')
    // for backward compatibility. Consumers may branch on err.name in their error handling.
    this.name = 'TokenForConnectionErrorCode';
  }
}

/**
 * Error thrown when a Token Exchange flow fails. This can occur due to misconfiguration,
 * an invalid subject_token, or if the exchange is denied by the server.
 */
export class TokenExchangeError extends ApiError {
  constructor(message: string, cause?: OAuth2Error) {
    super('token_exchange_error', message, cause);
    this.name = 'TokenExchangeError';
  }
}

/**
 * Error thrown when verifying the logout token.
 */
export class VerifyLogoutTokenError extends Error {
  public code: string = 'verify_logout_token_error';

  constructor(message: string) {
    super(message);
    this.name = 'VerifyLogoutTokenError';
  }
}

/**
 * Error thrown when trying to use Client-Initiated Backchannel Authentication.
 */
export class BackchannelAuthenticationError extends ApiError {
  public code: string = 'backchannel_authentication_error';

  constructor(cause?: OAuth2Error) {
    super(
      'backchannel_authentication_error',
      'There was an error when trying to use Client-Initiated Backchannel Authentication.',
      cause
    );
    this.name = 'BackchannelAuthenticationError';
  }
}

/**
 * Error thrown when trying to build the authorization URL.
 */
export class BuildAuthorizationUrlError extends ApiError {
  constructor(cause?: OAuth2Error) {
    super('build_authorization_url_error', 'There was an error when trying to build the authorization URL.', cause);
    this.name = 'BuildAuthorizationUrlError';
  }
}

/**
 * Error thrown when trying to build the Link User URL.
 */
export class BuildLinkUserUrlError extends ApiError {
  constructor(cause?: OAuth2Error) {
    super('build_link_user_url_error', 'There was an error when trying to build the Link User URL.', cause);
    this.name = 'BuildLinkUserUrlError';
  }
}

/**
 * Error thrown when trying to build the Unlink User URL.
 */
export class BuildUnlinkUserUrlError extends ApiError {
  constructor(cause?: OAuth2Error) {
    super('build_unlink_user_url_error', 'There was an error when trying to build the Unlink User URL.', cause);
    this.name = 'BuildUnlinkUserUrlError';
  }
}

/**
 * Narrows an error thrown by a token request method to one caused by an `mfa_required` response.
 *
 * When the Auth0 server requires multi-factor authentication, token request methods
 * (`getTokenByPassword`, `getTokenByRefreshToken`, `exchangeToken`, `passkey.getTokenByPasskey`)
 * throw their usual error (e.g. `TokenByPasswordError`, `PasskeyGetTokenError`) with
 * `cause.error` set to `'mfa_required'`.
 * The `cause` will also contain:
 * - `mfa_token` — the token needed to proceed with enrollment or challenge MFA APIs
 * - `mfa_requirements` — (optional) describes which factors to challenge or enroll
 *
 * This type guard checks whether an error was caused by an `mfa_required` response and
 * narrows the type so that `cause` and `cause.mfa_token` are guaranteed to be defined.
 *
 * @param error - The error caught from a token request method
 * @returns `true` if the error was caused by an `mfa_required` server response
 *
 * @example
 * ```typescript
 * import { AuthClient, isMfaRequiredError } from '@auth0/auth0-auth-js';
 *
 * try {
 *   await authClient.getTokenByPassword({ username, password });
 * } catch (error) {
 *   if (isMfaRequiredError(error)) {
 *     // error.cause.mfa_token is guaranteed to be defined here
 *     const challenge = await authClient.mfa.challengeAuthenticator({
 *       mfaToken: error.cause.mfa_token,
 *       challengeType: 'otp',
 *     });
 *   }
 * }
 * ```
 */
export interface MfaRequiredError extends Error {
  code: string;
  cause: OAuth2Error & { error: 'mfa_required'; mfa_token: string; mfa_requirements?: MfaRequirements };
}

export function isMfaRequiredError(error: unknown): error is MfaRequiredError {
  return (
    error instanceof Error &&
    (error as { cause?: OAuth2Error }).cause?.error === 'mfa_required' &&
    typeof (error as { cause?: OAuth2Error }).cause?.mfa_token === 'string'
  );
}

/**
 * Error thrown when Client Secret or Client Assertion Signing Key is missing.
 */
export class MissingClientAuthError extends Error {
  public code: string = 'missing_client_auth_error';

  constructor() {
    super('The client secret or client assertion signing key must be provided.');
    this.name = 'MissingClientAuthError';
  }
}
